import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  Typography,
} from "@mui/material";
import MovieIcon from "@mui/icons-material/Movie";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import YouTubeIcon from "@mui/icons-material/YouTube";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { selectCurrentToken } from "../../../auth/authSlice";
import { getApiBaseUrl } from "../../../../../helpers/apiUrl";

const contentStyles = {
  color: "text.primary",
  fontSize: "1rem",
  lineHeight: 1.7,
  "& p": { margin: "0 0 1em 0" },
  "& h1, & h2, & h3, & h4, & h5, & h6": { marginTop: "1em", marginBottom: "0.75em", fontWeight: 700 },
  "& ul, & ol": { margin: "0 0 1em 0", paddingLeft: "1.25em" },
  "& li": { marginBottom: "0.5em" },
  "& code": {
    backgroundColor: (theme) => theme.palette.mode === "dark" ? "#2a2a2a" : "#f5f5f5",
    padding: "0.2em 0.4em",
    borderRadius: "3px",
    fontFamily: "monospace",
    fontSize: "0.9em",
  },
  "& pre": {
    backgroundColor: (theme) => theme.palette.mode === "dark" ? "#2a2a2a" : "#f5f5f5",
    padding: "1em",
    borderRadius: "4px",
    overflow: "auto",
    marginBottom: "1em",
  },
  "& blockquote": {
    borderLeft: (theme) => `3px solid ${theme.palette.mode === "dark" ? "#555" : "#ddd"}`,
    paddingLeft: "1em",
    marginLeft: 0,
    color: "text.secondary",
    fontStyle: "italic",
  },
};

const getAttachmentIcon = (type) => {
  if (type === "pdf") return <PictureAsPdfIcon color="error" />;
  if (type === "youtube") return <YouTubeIcon color="error" />;
  return <MovieIcon color="primary" />;
};

const getAttachmentLabel = (type) => {
  if (type === "pdf") return "PDF";
  if (type === "youtube") return "YouTube";
  return "Video";
};

const getYouTubeEmbedUrl = (url) => {
  try {
    const parsedUrl = new URL(url);
    const host = parsedUrl.hostname.replace(/^www\./, "");
    let videoId = "";

    if (host === "youtu.be") {
      videoId = parsedUrl.pathname.split("/").filter(Boolean)[0];
    } else if (host === "youtube.com" || host === "m.youtube.com") {
      if (parsedUrl.pathname.startsWith("/embed/")) {
        videoId = parsedUrl.pathname.split("/").filter(Boolean)[1];
      } else if (parsedUrl.pathname.startsWith("/shorts/")) {
        videoId = parsedUrl.pathname.split("/").filter(Boolean)[1];
      } else {
        videoId = parsedUrl.searchParams.get("v");
      }
    }

    return videoId ? `https://www.youtube.com/embed/${videoId}` : "";
  } catch {
    return "";
  }
};

const YouTubePreview = ({ resource }) => {
  const embedUrl = getYouTubeEmbedUrl(resource.url);

  if (!embedUrl) {
    return (
      <Box
        sx={{
          mt: 2,
          p: 2,
          borderRadius: "10px",
          backgroundColor: (theme) => theme.palette.mode === "dark" ? "#1e1e1e" : "#F9F9F9",
          color: "text.secondary",
        }}
      >
        <Typography variant="body2">This YouTube link could not be embedded. Open it in a new tab instead.</Typography>
      </Box>
    );
  }

  return (
    <Box
      component="iframe"
      src={embedUrl}
      title={resource.name || "YouTube attachment"}
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
      allowFullScreen
      sx={{
        mt: 2,
        width: "100%",
        aspectRatio: "16 / 9",
        border: 0,
        borderRadius: "10px",
        backgroundColor: "#000",
      }}
    />
  );
};

const PdfPreview = ({ resource }) => {
  const accessToken = useSelector(selectCurrentToken);
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewError, setPreviewError] = useState("");

  useEffect(() => {
    if (!resource?.id) {
      setPreviewUrl(resource?.url || "");
      return undefined;
    }

    let objectUrl = "";
    const controller = new AbortController();

    const loadPreview = async () => {
      setPreviewError("");
      try {
        const response = await fetch(`${getApiBaseUrl()}/resource/${resource.id}/preview`, {
          headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
          signal: controller.signal,
        });
        if (!response.ok) {
          throw new Error("PDF preview could not be loaded");
        }
        const blob = await response.blob();
        objectUrl = URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));
        setPreviewUrl(objectUrl);
      } catch (error) {
        if (error.name === "AbortError") return;
        setPreviewError("PDF preview could not be loaded. Open the file in a new tab instead.");
        setPreviewUrl("");
      }
    };

    loadPreview();

    return () => {
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [accessToken, resource?.id, resource?.url]);

  if (previewError) {
    return (
      <Box
        sx={{
          mt: 2,
          p: 2,
          borderRadius: "10px",
          backgroundColor: (theme) => theme.palette.mode === "dark" ? "#1e1e1e" : "#F9F9F9",
          color: "text.secondary",
        }}
      >
        <Typography variant="body2">{previewError}</Typography>
      </Box>
    );
  }

  if (!previewUrl) {
    return (
      <Box
        sx={{
          mt: 2,
          height: { xs: 180, md: 240 },
          borderRadius: "10px",
          backgroundColor: (theme) => theme.palette.mode === "dark" ? "#1e1e1e" : "#F9F9F9",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "text.secondary",
        }}
      >
        <Typography variant="body2">Loading PDF preview...</Typography>
      </Box>
    );
  }

  return (
    <Box
      component="iframe"
      src={previewUrl}
      title={resource.name || "PDF attachment"}
      sx={{
        mt: 2,
        width: "100%",
        height: { xs: 320, md: 460 },
        border: 0,
        borderRadius: "10px",
        backgroundColor: (theme) => theme.palette.mode === "dark" ? "#1e1e1e" : "#F9F9F9",
      }}
    />
  );
};

export const NoteAttachments = ({ resources = [] }) => {
  const attachments = resources.filter((resource) => ["pdf", "video", "youtube"].includes(resource.type));

  if (!attachments.length) return null;

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
        Attachments
      </Typography>
      <Stack spacing={2}>
        {attachments.map((resource) => (
          <Card
            key={resource.id || resource.url}
            variant="outlined"
            sx={{ borderRadius: "12px", overflow: "hidden" }}
          >
            <CardContent>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  {getAttachmentIcon(resource.type)}
                  <Box>
                    <Typography sx={{ fontWeight: 600 }}>
                      {resource.name || "Attachment"}
                    </Typography>
                    <Chip
                      label={getAttachmentLabel(resource.type)}
                      size="small"
                      variant="outlined"
                      sx={{ mt: 0.75 }}
                    />
                  </Box>
                </Stack>
                <Button
                  href={resource.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  size="small"
                  endIcon={<OpenInNewIcon />}
                >
                  Open
                </Button>
              </Stack>

              {resource.type === "video" && (
                <Box
                  component="video"
                  src={resource.url}
                  controls
                  sx={{
                    mt: 2,
                    width: "100%",
                    maxHeight: 420,
                    borderRadius: "10px",
                    backgroundColor: "#000",
                  }}
                />
              )}

              {resource.type === "pdf" && (
                <PdfPreview resource={resource} />
              )}

              {resource.type === "youtube" && (
                <YouTubePreview resource={resource} />
              )}
            </CardContent>
          </Card>
        ))}
      </Stack>
    </Box>
  );
};

const NotePreviewContent = ({ note }) => {
  return (
    <Box>
      <Box
        sx={contentStyles}
        dangerouslySetInnerHTML={{ __html: note?.body || "" }}
      />
      <NoteAttachments resources={note?.resources || []} />
    </Box>
  );
};

export default NotePreviewContent;
